import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './Button';

/**
 * Dialogs and drawers.
 *
 * Built on the native `<dialog>` element so focus trapping, the top layer and Escape handling come
 * from the platform rather than from hand-written key handlers that miss cases. Focus is restored
 * to the trigger on close, which is what keyboard users need to carry on where they were.
 */

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Blocks Escape and backdrop dismissal - used while a submission is in flight. */
  busy?: boolean;
}

const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  busy = false,
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  // Unique per dialog: closed dialogs stay in the DOM, and a shared id names every dialog after the first wrongly.
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      previouslyFocused.current = document.activeElement as HTMLElement | null;
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
      previouslyFocused.current?.focus();
    }
  }, [open]);

  const handleCancel = useCallback(
    (event: React.SyntheticEvent<HTMLDialogElement>) => {
      // `cancel` fires on Escape. Prevented while busy so a half-finished mutation is not
      // abandoned by a stray keypress.
      event.preventDefault();
      if (!busy) onClose();
    },
    [busy, onClose],
  );

  return (
    <dialog
      ref={dialogRef}
      onCancel={handleCancel}
      onClick={(event) => {
        // Only a click on the backdrop itself dismisses; a click inside must not.
        if (event.target === dialogRef.current && !busy) onClose();
      }}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      className={cn(
        // `m-auto` is what centres a modal dialog. The browser's own stylesheet gives <dialog>
        // `margin: auto`, and Tailwind's reset zeroes every margin - without this the dialog is
        // pinned to the top-left corner of the viewport.
        'm-auto w-[calc(100vw-2rem)] rounded-xl border border-border-subtle bg-surface p-0 text-text-primary shadow-xl',
        'backdrop:bg-overlay backdrop:backdrop-blur-[2px]',
        'animate-in-scale',
        SIZES[size],
      )}
    >
      <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-5 py-4">
        <div className="min-w-0">
          <h2 id={titleId} className="text-h2 text-text-primary">
            {title}
          </h2>
          {description && (
            <p id={descriptionId} className="mt-1 text-small text-text-muted">
              {description}
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          disabled={busy}
          aria-label="Close dialog"
        >
          <X className="size-4" />
        </Button>
      </div>

      {children && <div className="max-h-[65vh] overflow-y-auto px-5 py-4">{children}</div>}

      {footer && (
        <div className="flex flex-wrap justify-end gap-2 border-t border-border-subtle bg-surface-sunken px-5 py-3.5">
          {footer}
        </div>
      )}
    </dialog>
  );
}

/* ============================================================================================
   Confirmation
   ============================================================================================ */

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /** State the consequence plainly. "This cannot be undone" is worth the words. */
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  children?: ReactNode;
  confirmDisabled?: boolean;
}

/**
 * A confirmation step for actions with consequences.
 *
 * Destructive actions get a visually distinct treatment and a warning icon, because the difference
 * between "Save" and "Retire this asset" should be obvious before the click, not after.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
  children,
  confirmDisabled = false,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      busy={loading}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex gap-3">
        {destructive && (
          <span
            aria-hidden
            className="grid size-9 shrink-0 place-items-center rounded-full border border-danger-border bg-danger-subtle text-danger-text"
          >
            <AlertTriangle className="size-4.5" />
          </span>
        )}
        <div className="min-w-0 flex-1 space-y-3">
          <div className="text-body text-text-secondary">{message}</div>
          {children}
        </div>
      </div>
    </Dialog>
  );
}

/* ============================================================================================
   Drawer
   ============================================================================================ */

/** A right-hand panel for detail and secondary flows that should not lose the page behind them. */
export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = 'md',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'md' | 'lg';
}) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    // Stops the page behind scrolling while the drawer is open.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="absolute inset-0 bg-overlay backdrop-blur-[2px] animate-in-fade"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          'relative flex h-full w-full flex-col border-l border-border-subtle bg-surface shadow-xl animate-in-slide-right',
          width === 'md' ? 'sm:max-w-md' : 'sm:max-w-2xl',
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-h2 text-text-primary">{title}</h2>
            {description && <p className="mt-1 text-small text-text-muted">{description}</p>}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close panel">
            <X className="size-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 border-t border-border-subtle bg-surface-sunken px-5 py-3.5">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ------------------------------------------------------------------ menu

/**
 * A popover menu hung off a trigger.
 *
 * One primitive rather than three: the export formats, the global create shortcuts and the page
 * settings panel are all "a button that opens a small panel anchored under itself", and writing
 * that three times is how the three drift apart in behaviour.
 *
 * Dismissal is handled here - outside click and Escape - because a popover that only closes on its
 * own items is the single most common way these end up stranded open on a page.
 *
 * The panel is rendered through a portal and positioned against the trigger's box rather than being
 * absolutely placed inside it. An absolutely-positioned panel is clipped by any ancestor with
 * `overflow: hidden`, and the page hero is exactly that - it has to be, because its background mesh
 * is drawn by pseudo-elements that overflow the header on every side. Inside it, an in-flow menu
 * appeared as a two-pixel sliver.
 */
export function Menu({
  trigger,
  children,
  align = 'end',
  className,
  panelClassName,
  triggerClassName,
  label,
}: {
  /** Rendered as the button's content. */
  trigger: ReactNode;
  /** Receives `close` so an item can dismiss the menu after acting. */
  children: (close: () => void) => ReactNode;
  align?: 'start' | 'end';
  className?: string;
  panelClassName?: string;
  /** Replaces the default bordered-button look of the trigger. */
  triggerClassName?: string;
  label: string;
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; right: number } | null>(
    null,
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition({
      top: rect.bottom + 6,
      left: rect.left,
      right: window.innerWidth - rect.right,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        // Escape should land the user back on the trigger, not nowhere.
        triggerRef.current?.focus();
      }
    };
    // The panel is positioned in viewport coordinates, so it has to follow the trigger when the
    // page moves underneath it rather than floating where it was opened.
    const reposition = () => place();

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, place]);

  return (
    <div className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((current) => !current)}
        className={
          triggerClassName ??
          'inline-flex h-9 items-center gap-2 rounded-md border border-border-default bg-surface px-3 text-sm font-medium text-text-primary shadow-xs transition-colors hover:bg-surface-hover active:bg-surface-active [&_svg]:size-4'
        }
      >
        {trigger}
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            style={
              align === 'end'
                ? { top: position.top, right: position.right }
                : { top: position.top, left: position.left }
            }
            className={cn(
              'fixed z-100 max-h-[70vh] min-w-56 overflow-y-auto rounded-xl border border-border-default bg-surface-elevated p-1.5 shadow-lg',
              panelClassName,
            )}
          >
            {children(() => setOpen(false))}
          </div>,
          document.body,
        )}
    </div>
  );
}

/** A row inside a `Menu`. */
export function MenuItem({
  icon,
  children,
  description,
  onClick,
  disabled = false,
}: {
  icon?: ReactNode;
  children: ReactNode;
  description?: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-text-muted"
    >
      {icon && <span className="mt-0.5 grid place-items-center">{icon}</span>}
      <span className="min-w-0">
        <span className="block text-small font-medium text-text-primary">{children}</span>
        {description && <span className="block text-caption text-text-muted">{description}</span>}
      </span>
    </button>
  );
}

/** A heading separating groups of menu items. */
export function MenuLabel({ children }: { children: ReactNode }) {
  return <p className="px-2.5 pt-2 pb-1 text-label text-text-muted">{children}</p>;
}
